import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Loader2, Megaphone, X } from 'lucide-react';
import {
  fetchNotifications,
  fetchNotificationsUnreadCount,
  markNotificationRead,
  markNotificationDismissed,
  fetchActiveAnnouncements,
  dismissAnnouncement,
  type NotificationView,
} from '@/lib/api';
import type { AnnouncementPublic } from '@evi/shared';

type BellTab = 'activity' | 'news';
import { useAuthStore } from '@/store/auth';
import { useAnnouncementsStore } from '@/store/announcements';
import NamedIcon from './ui/NamedIcon';
import styles from './NotificationBell.module.css';

/**
 * PWA notification bell + dropdown panel.
 *
 * Mirrors the admin's NotificationBell shape (apps/admin/src/
 * components/notifications/NotificationBell.tsx) but rebuilt
 * against PWA design tokens + with a portal-rendered dropdown
 * to avoid the CSS containing-block trap that comes with the
 * AppShell's sticky top bar (any transform/filter/position-
 * sticky ancestor of an `absolute`-positioned dropdown becomes
 * its containing block, mis-anchoring the panel). Portaling to
 * document.body sidesteps that — the panel's `position: fixed`
 * resolves against the viewport regardless of ancestor layout.
 *
 * Differences from the admin component:
 *   - Portal-rendered dropdown (admin uses a relative-anchored
 *     absolute panel; works there because the admin nav slot
 *     has no transform ancestors).
 *   - "View all notifications" link at the bottom of the panel
 *     → routes to /notifications (existing PWA full-page list).
 *     Admin has no such page; the bell IS the surface there.
 *   - Reuses the PWA's optimistic mark-read / mark-dismissed
 *     mutation hooks from useNotifications.ts where it makes
 *     sense, but the local-row optimistic state stays inline
 *     for tighter dropdown UX (instant remove on dismiss without
 *     waiting for the React Query invalidation round-trip).
 *
 * Polling cadence matches admin: 30s for unread-count (cheap
 * server-side), lazy fetch for the list payload on dropdown
 * open with a 20-row cap and 10s staleTime.
 */

const POLL_INTERVAL_MS = 30_000;
const LIST_LIMIT = 20;

interface NotificationBellProps {
  onNavigate: (path: string) => void;
}

export function NotificationBell({ onNavigate }: NotificationBellProps) {
  const { t } = useTranslation('notificationBell');
  const qc = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Position the portal-rendered panel against the bell button.
  // Recomputed on open + on resize while open; the panel is
  // dismissed on scroll so we don't bother chasing the bell as
  // the page scrolls under the (sticky) top bar.
  //
  // Top-bar geometry: bell is the RIGHTMOST element in the top
  // bar's right cluster (theme picker sits to its left). So the
  // bell's `rect.right` is close to the viewport's right edge,
  // separated only by the top bar's 18px (28px ≥900px) right
  // padding. If you ever swap the order back so something else
  // sits to the right of the bell, the wide-mode anchor math
  // still works (Math.max guard prevents negative right values),
  // but the panel will detach further from the bell visually —
  // narrow mode is bell-position-agnostic and unaffected.
  //
  // Two layout modes:
  //   - Wide (viewport >= 500px): right-anchor under the bell at
  //     the panel's natural width (CSS .panel width: 380px). The
  //     `right` value places the panel's right edge level with
  //     the bell's right edge, growing leftward. Floor of 8px
  //     guards against any pathologically-positioned bell.
  //   - Narrow (viewport < 500px): right-anchoring at panel
  //     width 356px (calc(100vw - 24px)) on a 380px PWA viewport
  //     would still leave only ~6px of left-edge clearance, and
  //     a slightly wider phone (414, 430) makes the panel feel
  //     hugged-to-the-right. Switch to a stretched full-width-
  //     minus-32 layout via inline `left: 16; right: 16; width:
  //     auto` — the panel spans both sides of the viewport with
  //     16px margins, vertically anchored just below the bell.
  type Anchor =
    | { mode: 'wide'; top: number; right: number }
    | { mode: 'narrow'; top: number };
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  // Unread count polled every 30s. Gated on auth so the bell
  // doesn't 401 on the (impossible-but-defensive) case of the
  // top bar rendering pre-auth. Failing soft on errors so a
  // flaky API never breaks the navbar.
  const countQ = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => fetchNotificationsUnreadCount().catch(() => 0),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
    enabled: isAuthenticated,
  });
  // Active announcements live in the same bell. Shared query key with the
  // map overlay (AnnouncementCard) so the two dedupe and a dismissal in one
  // reflects in the other. Fetched whenever authed (not gated on open) so
  // the badge can reflect an unseen announcement before the panel opens.
  const annQ = useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: () => fetchActiveAnnouncements().catch(() => []),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const bellSeen = useAnnouncementsStore((s) => s.bellSeen);
  const markBellSeen = useAnnouncementsStore((s) => s.markBellSeen);
  const presentAnnouncement = useAnnouncementsStore((s) => s.present);

  // A newly published announcement bumps the badge until the driver opens
  // the bell (bellSeen), on top of the server's notification unread count.
  const [annDismissed, setAnnDismissed] = useState<Set<string>>(new Set());
  const unseenAnnouncements = (annQ.data ?? []).filter(
    (a) => !bellSeen.includes(a.id) && !annDismissed.has(a.id),
  ).length;
  const unreadCount = (countQ.data ?? 0) + unseenAnnouncements;

  // List fetched lazily — only when the dropdown opens. The
  // count query covers the badge case without the list payload,
  // so the navbar mounts cheap.
  const listQ = useQuery({
    queryKey: ['notifications', 'list', LIST_LIMIT],
    queryFn: () => fetchNotifications({ limit: LIST_LIMIT }),
    enabled: open && isAuthenticated,
    staleTime: 10_000,
  });

  // Opening the bell marks every listed announcement as seen so the badge
  // stops counting them (they still show until permanently dismissed).
  useEffect(() => {
    if (open && annQ.data && annQ.data.length > 0) {
      markBellSeen(annQ.data.map((a) => a.id));
    }
  }, [open, annQ.data, markBellSeen]);

  // Prune the optimistic-dismiss set to ids the server still returns. An id the
  // server has dropped (dismissal committed) leaves the set; an id still present
  // stays hidden. Keying on data (not dataUpdatedAt) means a same-data background
  // refetch landing mid-POST can't resurrect a just-dismissed row.
  useEffect(() => {
    if (!annQ.data) return;
    const present = new Set(annQ.data.map((a) => a.id));
    setAnnDismissed((prev) => {
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [annQ.data]);

  const visibleAnnouncements = useMemo<AnnouncementPublic[]>(
    () => (annQ.data ?? []).filter((a) => !annDismissed.has(a.id)),
    [annQ.data, annDismissed],
  );

  // Tapping an announcement re-presents its overlay: route to the map (where
  // the overlay lives) and set the forced id the overlay consumes.
  const handleAnnouncementClick = (a: AnnouncementPublic): void => {
    presentAnnouncement(a.id);
    setOpen(false);
    onNavigate('/map');
  };

  // The bell's X on an announcement is the PERMANENT dismiss (unlike the
  // overlay X, which is session-only) — it retires the announcement everywhere.
  const handleAnnouncementDismiss = async (
    a: AnnouncementPublic,
    e: React.MouseEvent,
  ): Promise<void> => {
    e.stopPropagation();
    setAnnDismissed((prev) => new Set(prev).add(a.id));
    try {
      await dismissAnnouncement(a.id);
    } catch {
      setAnnDismissed((prev) => {
        const next = new Set(prev);
        next.delete(a.id);
        return next;
      });
    }
    void qc.invalidateQueries({ queryKey: ['announcements', 'active'] });
  };

  // Local optimistic-update layer over the React Query cache.
  // A dismiss removes the row immediately even before the
  // network round-trip completes; cache invalidation triggers
  // the fresh fetch but the row stays visible during that gap
  // without this.
  const [optimisticDismissed, setOptimisticDismissed] = useState<Set<string>>(new Set());
  const [optimisticRead, setOptimisticRead] = useState<Set<string>>(new Set());

  // Reset optimistic state when the list refetches — fresh
  // server data is the new source of truth.
  useEffect(() => {
    if (listQ.dataUpdatedAt > 0) {
      setOptimisticDismissed(new Set());
      setOptimisticRead(new Set());
    }
  }, [listQ.dataUpdatedAt]);

  // Anchor + dismiss handlers — kept in one effect so they
  // mount/unmount in lockstep with `open`.
  useEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    const computeAnchor = (): void => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const top = rect.bottom + 8;
      // Threshold matches typical PWA breakpoint: phones (~380-
      // 430px) hit narrow mode, tablets and up hit wide mode.
      if (window.innerWidth < 500) {
        setAnchor({ mode: 'narrow', top });
      } else {
        setAnchor({
          mode: 'wide',
          top,
          right: Math.max(8, window.innerWidth - rect.right),
        });
      }
    };
    computeAnchor();
    // Mousedown (not click) so the dismiss fires before any
    // click handlers inside the panel — gives tappable rows a
    // chance to handle their own dismissal cleanly without
    // racing the close.
    const onMouseDown = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      const inButton = buttonRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inButton && !inPanel) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Close on scroll — a sticky-top-bar bell with a viewport-
    // anchored panel that doesn't follow scroll would visually
    // detach. Cheaper to dismiss than to chase.
    const onScroll = (): void => setOpen(false);

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', computeAnchor);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', computeAnchor);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  const visibleList = useMemo<NotificationView[]>(() => {
    if (!listQ.data) return [];
    return listQ.data.filter((n) => !optimisticDismissed.has(n._id));
  }, [listQ.data, optimisticDismissed]);

  // ── Two tabs, split by whether the driver can act on the row ──────────
  // The server stamps every row with its tab (notification registry,
  // bellTabFor): charging, money, charger alerts, account and people are
  // `activity`; offers, announcements and rewards are `news`. Announcements
  // never pass through the registry, so they are placed here, under News.
  // The badge on the bell stays the total; each tab shows its own unread
  // count so a driver can see where the pull is without opening both.
  // News & rewards leads (Warren, 2026-09-16): it is the tab we put in
  // front of the driver, so it is first and it is where the bell opens.
  const [tab, setTab] = useState<BellTab>('news');
  const activityList = useMemo(() => visibleList.filter((n) => n.tab !== 'news'), [visibleList]);
  const newsList = useMemo(() => visibleList.filter((n) => n.tab === 'news'), [visibleList]);
  const isUnreadRow = useCallback(
    (n: NotificationView) => !n.read_at && !optimisticRead.has(n._id),
    [optimisticRead],
  );
  const activityUnread = activityList.filter(isUnreadRow).length;
  const newsUnread = newsList.filter(isUnreadRow).length + unseenAnnouncements;
  // Land on the tab that has something new; news wins a tie.
  useEffect(() => {
    if (!open || !listQ.data) return;
    if (newsUnread === 0 && activityUnread > 0) setTab('activity');
    // Deliberately keyed on open + the fetched list only: re-running on every
    // unread-count change would yank the driver between tabs while reading.
  }, [open, listQ.data]);
  const tabList = tab === 'news' ? newsList : activityList;
  const tabAnnouncements = tab === 'news' ? visibleAnnouncements : [];

  const invalidate = useCallback((): void => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  }, [qc]);

  const handleRead = async (n: NotificationView): Promise<void> => {
    if (n.read_at || optimisticRead.has(n._id)) return;
    setOptimisticRead((prev) => new Set(prev).add(n._id));
    try {
      await markNotificationRead(n._id);
    } catch {
      // Roll back on failure so the dot reappears.
      setOptimisticRead((prev) => {
        const next = new Set(prev);
        next.delete(n._id);
        return next;
      });
    }
    invalidate();
  };

  const handleDismiss = async (
    n: NotificationView,
    e: React.MouseEvent,
  ): Promise<void> => {
    e.stopPropagation();
    setOptimisticDismissed((prev) => new Set(prev).add(n._id));
    try {
      await markNotificationDismissed(n._id);
    } catch {
      setOptimisticDismissed((prev) => {
        const next = new Set(prev);
        next.delete(n._id);
        return next;
      });
    }
    invalidate();
  };

  const handleRowClick = async (n: NotificationView): Promise<void> => {
    await handleRead(n);
    if (n.link) {
      setOpen(false);
      onNavigate(n.link);
    }
  };

  const handleMarkAllRead = async (): Promise<void> => {
    const unread = visibleList.filter((n) => !n.read_at && !optimisticRead.has(n._id));
    if (unread.length === 0) return;
    setOptimisticRead((prev) => {
      const next = new Set(prev);
      for (const n of unread) next.add(n._id);
      return next;
    });
    // Sequential rather than Promise.all — keeps the server
    // happy under v1 scale (a driver with 50 unread is the
    // realistic worst case). A future batch endpoint is the
    // right scale-up.
    for (const n of unread) {
      try {
        await markNotificationRead(n._id);
      } catch {
        // Single-row failures don't roll back optimistic state —
        // invalidate at the end picks up the truth.
      }
    }
    invalidate();
  };

  const handleViewAll = (): void => {
    setOpen(false);
    onNavigate('/notifications');
  };

  const onRowKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    n: NotificationView,
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void handleRowClick(n);
    }
  };

  const showLoading = listQ.isLoading && !listQ.data;
  const showError = listQ.isError && !listQ.data;
  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.bellBtn}
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unreadCount > 0
            ? t('ariaUnread', { count: unreadCount })
            : t('title')
        }
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <NamedIcon name="bell" fallback={<Bell size={18} />} size={18} />
        {unreadCount > 0 && (
          <span className={styles.badge} aria-hidden>
            {badgeText}
          </span>
        )}
      </button>

      {open && anchor && createPortal(
        <div
          ref={panelRef}
          role="menu"
          aria-label={t('title')}
          className={styles.panel}
          style={
            anchor.mode === 'wide'
              ? { top: `${anchor.top}px`, right: `${anchor.right}px` }
              : {
                  // Narrow: stretch between viewport edges with 16px
                  // gutters. width:auto overrides the CSS width: 380px
                  // so left+right control the size; the panel ends up
                  // at viewport - 32px regardless of bell position.
                  top: `${anchor.top}px`,
                  left: '16px',
                  right: '16px',
                  width: 'auto',
                }
          }
        >
          <header className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>{t('title')}</h2>
            <button
              type="button"
              className={styles.markAllBtn}
              onClick={() => void handleMarkAllRead()}
              disabled={visibleList.every((n) => n.read_at || optimisticRead.has(n._id))}
            >
              {t('markAllRead')}
            </button>
          </header>
          <div className={styles.tabs} role="tablist" aria-label={t('title')}>
            {(['news', 'activity'] as const).map((key) => {
              const count = key === 'activity' ? activityUnread : newsUnread;
              const selected = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`${styles.tab} ${selected ? styles.tabSelected : ''}`}
                  onClick={() => setTab(key)}
                >
                  {t(`tabs.${key}`)}
                  {count > 0 && (
                    <span className={styles.tabCount} aria-label={t('ariaUnread', { count })}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className={styles.body}>
            {showError && (
              <div className={styles.errorRow}>
                {t('error')}
              </div>
            )}

            {/* Announcements first — admin-published, above transactional
                notifications. Tap re-opens the overlay; the X here is the
                PERMANENT dismiss (the overlay X is session-only). */}
            {tabAnnouncements.map((a) => (
              <button
                key={`ann-${a.id}`}
                type="button"
                role="menuitem"
                className={`${styles.row} ${styles.announceRow}`}
                onClick={() => handleAnnouncementClick(a)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAnnouncementClick(a);
                  }
                }}
              >
                <div className={styles.rowMain}>
                  <span className={styles.rowEyebrow}>
                    <Megaphone size={11} /> {t('announcement')}
                  </span>
                  <span className={styles.rowTitle}>{a.title}</span>
                  <span className={styles.rowBody}>{a.body}</span>
                </div>
                <button
                  type="button"
                  className={styles.dismissBtn}
                  onClick={(e) => void handleAnnouncementDismiss(a, e)}
                  aria-label={t('dismissLabel', { title: a.title })}
                >
                  <X size={14} />
                </button>
              </button>
            ))}

            {showLoading ? (
              <div className={styles.loading}>
                <Loader2 size={14} className={styles.spin} /> {t('loading')}
              </div>
            ) : tabList.length === 0 ? (
              // Only "all caught up" when there's genuinely nothing on THIS
              // tab — not while the error row above is explaining a failed load.
              tabAnnouncements.length === 0 && !showError ? (
                <div className={styles.empty}>
                  <BellOff size={28} className={styles.emptyIcon} />
                  <span>{t(tab === 'news' ? 'emptyNews' : 'empty')}</span>
                </div>
              ) : null
            ) : (
              tabList.map((n) => {
                const isUnread = !n.read_at && !optimisticRead.has(n._id);
                return (
                  <button
                    key={n._id}
                    type="button"
                    role="menuitem"
                    className={`${styles.row} ${isUnread ? styles.rowUnread : ''}`}
                    onClick={() => void handleRowClick(n)}
                    onKeyDown={(e) => onRowKeyDown(e, n)}
                  >
                    <div className={styles.rowMain}>
                      <span className={styles.rowTitle}>{n.title}</span>
                      <span className={styles.rowBody}>{n.body}</span>
                      {/* Time of the LATEST occurrence, and "×N" when the
                          server folded repeats onto this one row — one
                          "Charger offline" at ×4 instead of four of them. */}
                      <span className={styles.rowMeta}>
                        {formatRelative(n.last_occurred_at ?? n.created_at, t)}
                        {n.occurrence_count > 1 && (
                          <span
                            className={styles.repeatChip}
                            aria-label={t('repeatLabel', { count: n.occurrence_count })}
                          >
                            {t('repeat', { count: n.occurrence_count })}
                          </span>
                        )}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.dismissBtn}
                      onClick={(e) => void handleDismiss(n, e)}
                      aria-label={t('dismissLabel', { title: n.title })}
                    >
                      <X size={14} />
                    </button>
                  </button>
                );
              })
            )}
          </div>

          {/* "View all" link routes to the existing /notifications
              page (commit 332bcf4). Pinned to the panel footer so
              it's always reachable regardless of list length. */}
          <footer className={styles.panelFooter}>
            <button
              type="button"
              className={styles.viewAllBtn}
              onClick={handleViewAll}
            >
              {t('viewAll')}
            </button>
          </footer>
        </div>,
        document.body,
      )}
    </>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatRelative(
  iso: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const then = new Date(iso);
  // The em-dash here is the documented null-value placeholder
  // convention used across the PWA (DashboardPage, HistoryPage,
  // SettingsPage, MyChargerEarningsPage, PreSessionPage,
  // StationDetailPage, FriendDetailPage, MyChargerDetailPage,
  // ProfilePage, all use the same standalone '—' to mean "no
  // value here"). Distinct from prose em-dashes (which were
  // purged in the copy-editorial pass — see
  // docs/audits/pwa-copy-audit-2026-05-07.md). Standalone null
  // markers are NOT prose; they're a UI symbol. This convention
  // is the one documented exception to the no-em-dash rule.
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return t('time.justNow');
  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 1) return t('time.justNow');
  if (minutes < 60) return t('time.minutes', { m: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('time.hours', { h: hours });
  if (hours < 48) return t('time.yesterday');
  const days = Math.floor(hours / 24);
  if (days < 30) return t('time.days', { d: days });
  return then.toLocaleDateString();
}
